import Link from "next/link";
import ListingPhoto from "@/components/listing-photo";
import {ListingBrokerage, RealtorLogo} from "@/components/listings";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardFooter} from "@/components/ui/card";
import {formatPrice, isPhotoUrl} from "@/lib/listing-filters";
import {listingPath, type ListingSummary} from "@/lib/listings";
import {ComingSoonPhoto} from "@/components/listings/coming-soon";

/** Everything one classic card shows; plain data, so a server page can hand it to the client carousel. */
export type ClassicCardData = {
    key: string;
    /** Where View goes. */
    href: string;
    newTab: boolean;
    /** The photo, or null for none (the site's "coming soon" picture is shown). */
    photo: { src: string; alt: string } | null;
    /** `cover` fills the frame (the home page carousel only, while the trim is on); `contain` shows the whole photo. */
    photoFit: "cover" | "contain";
    priority?: boolean;
    address: string | null;
    propertyType: string | null;
    mlsNumber: string | null;
    area: string | null;
    city: string | null;
    /** Already formatted. */
    price: string | null;
    bedrooms: number | string | null;
    bathrooms: number | string | null;
    yearBuilt: number | string | null;
    /** Required for DDF content: the listing brokerage and the REALTOR.ca logo (rules 6(a)(i), 6(a)(ii)). */
    compliance?: { officeName: string; listingUrl: string | null };
};

/**
 * The card data of a DDF listing. It always sets `compliance`, so a DDF card never lacks the
 * brokerage line and the logo.
 */
export function ddfCard(
    item: ListingSummary,
    opts: { newTab: boolean; priority?: boolean; fit?: "cover" | "contain" },
): ClassicCardData {
    const title = item.address ?? "Listing";
    return {
        key: item.listing_key,
        href: listingPath(item),
        newTab: opts.newTab,
        photo: item.photo && isPhotoUrl(item.photo.url)
            ? {src: item.photo.url, alt: item.photo.description || `Photo of ${title}`}
            : null,
        photoFit: opts.fit ?? "contain",
        priority: opts.priority,
        address: item.address,
        propertyType: item.property_type,
        mlsNumber: item.mls_number,
        area: item.city_region ?? item.subdivision,
        city: item.city,
        price: formatPrice(item.price),
        bedrooms: item.bedrooms,
        bathrooms: item.bathrooms,
        yearBuilt: item.year_built ?? null,
        compliance: {officeName: item.office_name, listingUrl: item.listing_url},
    };
}

/**
 * How a trimmed REALTOR.ca photo fills its card frame: centred, and enlarged a little so the
 * white bands CREA adds above and below many 3:2 photos (about 6% of the height each) are cut off
 * and the photo itself fills the card, as the old site's photos did. This may cut the REALTOR®
 * mark in the photo's top-left corner (the owner's decision, #176); the setting
 * `carousel_crop_photos` turns the trim off. The frame's `overflow-hidden` clips the enlargement.
 */
const CROP_REALTOR = "object-center scale-[1.15]";

const line = "text-center font-extralight leading-5 text-sm";

/**
 * The classic listing card: a grey card, a photo frame, centred lines, a Bedrooms | Bathroom |
 * Built row and a full-width View button. A card with `compliance` also shows "Listing provided
 * by <brokerage>", centred, below the row, and its 90 px REALTOR.ca logo, centred under the button. Photos set to
 * `contain` are shown whole in a 4:3 frame on the card's own grey; nothing is laid over a photo.
 */
export function ClassicListingCard({card}: { card: ClassicCardData }) {
    const sizes = "(min-width: 1024px) 33vw, (min-width: 464px) 50vw, 100vw";
    const contain = card.photoFit === "contain";
    return (
        <Card className="bg-zinc-100 flex h-full flex-col">
            <CardContent className="rounded grow flex flex-col pb-3">
                <div className={contain ? "relative mb-5 mt-5 aspect-[4/3] w-full" : "relative mb-5 mt-5 h-80 overflow-hidden rounded"}>
                    {card.photo ? (
                        <ListingPhoto src={card.photo.src} alt={card.photo.alt} sizes={sizes}
                                      fit={card.photoFit} priority={card.priority}
                                      className={contain ? undefined : CROP_REALTOR}/>
                    ) : (
                        <ComingSoonPhoto sizes={sizes}/>
                    )}
                </div>
                <div className="flex flex-col grow">
                    <div className="text-center font-semibold">{card.address}</div>
                    {card.propertyType ? <div className={line}>{card.propertyType}</div> : null}
                    {card.mlsNumber ? <div className={line}>MLS®: {card.mlsNumber}</div> : null}
                    {card.area ? <div className={line}>{card.area}</div> : null}
                    {card.city ? <div className={line}>{card.city}</div> : null}
                    {card.price ? <div className="text-center font-extralight font-serif">{card.price}</div> : null}
                    <hr className="w-full mt-5 m-auto"/>
                    <div className="flex flex-row w-full divide-x-2 justify-center text-center font-extralight text-sm">
                        <div className="flex flex-col basis-1/3">
                            <div>Bedrooms</div>
                            <div>{card.bedrooms ?? "N/A"}</div>
                        </div>
                        <div className="flex flex-col basis-1/3">
                            <div>Bathroom</div>
                            <div>{card.bathrooms ?? "N/A"}</div>
                        </div>
                        <div className="flex flex-col basis-1/3">
                            <div>Built</div>
                            <div>{card.yearBuilt ?? "N/A"}</div>
                        </div>
                    </div>
                    {card.compliance ? (
                        <div className="mt-auto pt-4 text-center text-sm">
                            <ListingBrokerage name={card.compliance.officeName}/>
                        </div>
                    ) : null}
                </div>
            </CardContent>
            <CardFooter className="flex flex-col items-center gap-4 pt-0">
                <Button className="w-full drop-shadow-xl bg-gray-500 hover:bg-wch-secondary-500" asChild>
                    <Link href={card.href} prefetch={false} target={card.newTab ? "_blank" : undefined}
                          aria-label={`View ${card.address ?? "listing"}${card.newTab ? " (opens in a new tab)" : ""}`}>
                        View
                    </Link>
                </Button>
                {/* Under the button, centred: the REALTOR.ca logo of this listing, 90 x 90, linking to it (rule 6(a)(i)). */}
                {card.compliance ? <RealtorLogo href={card.compliance.listingUrl}/> : null}
            </CardFooter>
        </Card>
    );
}
